import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useRole = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest, setError } = useHttp();

  const getRoles = async () => {
    try {
      const response = await sendRequest({
        url: '/api/v1/role',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const getRoleById = async roleId => {
    try {
      const response = await sendRequest({
        url: `/api/v1/role/${roleId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const updateRoleById = async (roleId, payload) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/role/${roleId}`,
        method: 'PATCH',
        headers: {
          ...authCtx.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: payload,
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const getRightsOverview = async () => {
    try {
      const response = await sendRequest({
        url: '/api/v1/role/rights-overview',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  // Grants the superAdmin role all available top-level rights as standard rights
  // on every device type. Authorized server-side by superAdmin role membership.
  const grantSuperAdminAllRights = async () => {
    try {
      const response = await sendRequest({
        url: '/api/v1/role/superadmin/grant-all-rights',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  // Grants an allowlisted privileged role (e.g. executiveLeadership, ITManager)
  // all available top-level rights as standard rights on every device type.
  // Authorized server-side by superAdmin role membership; the backend only
  // accepts roles on its allowlist.
  const grantRoleAllRights = async roleName => {
    try {
      const response = await sendRequest({
        url: `/api/v1/role/${encodeURIComponent(roleName)}/grant-all-rights`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const getRightsOverviewDetail = async right => {
    try {
      const response = await sendRequest({
        url: `/api/v1/role/rights-overview/detail?right=${encodeURIComponent(right)}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  return {
    isLoading,
    error,
    setError,
    getRoles,
    getRoleById,
    updateRoleById,
    getRightsOverview,
    getRightsOverviewDetail,
    grantSuperAdminAllRights,
    grantRoleAllRights,
  };
};

export default useRole;
